import { isPlatformBrowser } from '@angular/common';
import type { OnDestroy } from '@angular/core';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute } from '@freelancer/activated-route';
import { Datastore } from '@freelancer/datastore';
import type { TrackingConsentModeCollection } from '@freelancer/datastore/collections';
import { LocalStorage } from '@freelancer/local-storage';
import { Pwa } from '@freelancer/pwa';
import { TrackingConsent } from '@freelancer/tracking';
import { TrackingConsentStatus } from '@freelancer/tracking/interface';
import { isDefined } from '@freelancer/utils';
import { combineLatest, Subscription, take } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TrackingConsentBannerService implements OnDestroy {
  private isTrackingConsentBannerDisabled = false;
  private subscriptions = new Subscription();

  constructor(
    private datastore: Datastore,
    private localStorage: LocalStorage,
    private pwa: Pwa,
    private route: ActivatedRoute,
    private trackingConsent: TrackingConsent,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  // Only to be called by TrackingConsentBannerOverridesGuard
  setIsTrackingConsentBannerDisabled(disabled: boolean): void {
    this.isTrackingConsentBannerDisabled = disabled;
  }

  /**
   * Checks if tracking consent banner should be shown based on:
   * - tracking consent banner is not disabled via query param
   * - browser platform (non-SSR, non-native)
   * - user has not explicitly denied consent via query param
   * - user is in a country where consent mode should be enabled
   * - user is referred from Bing (ads via msclkid or _uetmsclkid cookie, or organic)
   */
  async isSupported(): Promise<boolean> {
    if (
      this.isTrackingConsentBannerDisabled ||
      !isPlatformBrowser(this.platformId) ||
      this.pwa.isInstalled()
    ) {
      return false;
    }
    // Check if consent has been denied via `no_third_party_tracking` query param
    const isConsentExplicitlyDenied =
      (await this.trackingConsent.getThirdPartyStatus(true)) ===
      TrackingConsentStatus.DENIED;

    if (isConsentExplicitlyDenied) {
      return false;
    }

    const trackingConsentMode$ = this.datastore
      .document<TrackingConsentModeCollection>('trackingConsentMode')
      .valueChanges();

    return new Promise<boolean>(resolve => {
      this.subscriptions.add(
        combineLatest([
          trackingConsentMode$,
          this.route.queryParams,
          this.localStorage.get('trackingConsent', true),
        ])
          .pipe(take(1))
          .subscribe(
            async ([
              trackingConsentMode,
              queryParams,
              loggedOutTrackingConsent,
            ]) => {
              // Check if user is in a regulated country
              const isInRegulatedCountry =
                trackingConsentMode?.isEnabled || false;

              if (!isInRegulatedCountry) {
                resolve(false);
              }

              // Check if user is referred from Bing
              const alphaNumericRegExp = /^[a-zA-Z0-9_-]+$/;
              const hasValidMsclkidQueryParam =
                queryParams.msclkid &&
                alphaNumericRegExp.test(queryParams.msclkid);

              // eslint-disable-next-line no-restricted-globals
              const hasMsclkidCookie = document.cookie
                .split(';')
                .some(c => c.trim().startsWith('_uetmsclkid='));

              const isReferrerFromBingOrganic =
                // eslint-disable-next-line no-restricted-globals
                document.referrer.includes('bing.com') ||
                /**
                 * When user is referred from Bing organically while logged
                 * out, the moment they log in, the referrer is lost. So,
                 * even if they consented to tracking while logged out,
                 * upon login, we won't know if they were referred from Bing
                 * or not. Thus, we check if there is a logged out tracking
                 * consent in local storage, and if so, we assume they
                 * were referred from Bing organically and provided consented.
                 *
                 * If the user was asked but ignored the banner, the moment
                 * they log in, referrer is lost so they won't be considered
                 * as referred from Bing organically.
                 */
                isDefined(loggedOutTrackingConsent);

              resolve(
                hasValidMsclkidQueryParam ||
                  hasMsclkidCookie ||
                  isReferrerFromBingOrganic,
              );
            },
          ),
      );
    });
  }

  setBingAdsDefaultConsentSetting(): void {
    /**
     * T314192
     *
     * To implement consent mode, we set the default consent setting for Bing Ads tracking
     * to denied. We then update ad_storage when the user provides or denies consent
     * in the tracking consent banner component, which is stored in local storage.
     */
    // eslint-disable-next-line no-restricted-globals, piggyback/no-restricted-global-extend
    window.uetq = window.uetq || [];
    // eslint-disable-next-line no-restricted-globals, piggyback/no-restricted-global-extend
    window.uetq.push('consent', 'default', {
      ad_storage: 'denied',
    });
  }

  updateBingAdsConsentSetting(consent: boolean | undefined): void {
    // eslint-disable-next-line no-restricted-globals, piggyback/no-restricted-global-extend
    window.uetq.push('consent', 'update', {
      ad_storage: consent ? 'granted' : 'denied',
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
